import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { IoMdSend } from 'react-icons/io';
import middleware from "../../Api/Middleware";
import ToastSuccess from "../../Toasts/ToastSuccess";
import ToastError from "../../Toasts/ToastError";
import "../ModalStyle.css";

import { BorderedTextInput} from "../../HouseStyle/Components/Inputs";
import {BorderedDropDown} from "../../HouseStyle/Components/DropDowns";
import close from "../../HouseStyle/Icons/Close.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import "../ModalStyle.css";
import { ButtonBlue, ButtonLightGrey } from '../../HouseStyle/Components/Buttons';
import { Title } from '@mui/icons-material';
import { template } from 'lodash';

// Constants for input limitations based on DB constraints
const MAX_EMAIL_LENGTH = 255;
const MAX_SUBJECT_LENGTH = 255;
const MAX_CONTENT_LENGTH = 5000;
const DEFAULT_SENDER_EMAIL = 'noreply@techdogsoftware.nl';

// wrappings
const wrapStyle = { overflowWrap: 'break-word' };
const contentStyle = { ...wrapStyle, whiteSpace: 'pre-wrap' };

// Helper function to create initial email data with proper order handling
const initialEmailData = (orderId, orderNumber) => ({
  order_id: orderId || '',
  receiver_email: '',
  subject: orderNumber ? `Nieuw bericht voor bestelling #${orderNumber}` : '',
  content: ''
});

function SendEmailModal({ order, emails, handleEmailSent }) {

  const [senderEmail, setSenderEmail] = useState(DEFAULT_SENDER_EMAIL);

  const [show, setShow] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [isCustomEmail, setIsCustomEmail] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [emailData, setEmailData] = useState(() => initialEmailData(order?.order_id, order?.order_number));
  const [touched, setTouched] = useState({
    receiver_email: false,
    subject: false,
    content: false
  });
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    const fetchCompanyEmail = async () => {
      try {
        const response = await middleware.get("/settings/backoffice");
        const settingsEmail = response?.data?.data?.[0]?.email;

        if (settingsEmail && validateEmail(settingsEmail)) {
          setSenderEmail(settingsEmail);
        } else {
          setSenderEmail(DEFAULT_SENDER_EMAIL);
        }
      } catch (error) {
        console.error("Failed to load sender email from settings", error);
        setSenderEmail(DEFAULT_SENDER_EMAIL);
      }
    };

    fetchCompanyEmail();
  }, []);

  const hasUnsavedChanges = () => {
    const currentOrderNumber = order?.order_number || '';
    const defaultSubject = currentOrderNumber ? `Nieuw bericht voor bestelling ${currentOrderNumber}` : '';

    return emailData.receiver_email !== '' ||
      emailData.subject !== defaultSubject ||
      emailData.content !== '';
  };

  useEffect(() => {
    if (order) {
      setEmailData(prevData => ({
        ...prevData,
        order_id: order.order_id || '',
        subject: order.order_number ? `Nieuw bericht voor bestelling #${order.order_number}` : prevData.subject
      }));
    }
  }, [order]);

  const handleClose = () => {
    closeModal();
  };

  const closeModal = () => {
    setShow(false);
    setIsCustomEmail(false);
    setEmailError('');
    setShowPreview(false);
    setEmailData(initialEmailData(order?.order_id, order?.order_number));
    setTouched({
      receiver_email: false,
      subject: false,
      content: false
    });
  };

  const handleShow = () => setShow(true);

  const validateEmail = (email) => {
    if (email.length > MAX_EMAIL_LENGTH) {
      return false;
    }
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailInputChange = (e) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    if (name === 'subject' && value.length > MAX_SUBJECT_LENGTH) {
      ToastError(`Onderwerp mag niet langer zijn dan ${MAX_SUBJECT_LENGTH} tekens`);
      return;
    }

    if (name === 'content' && value.length > MAX_CONTENT_LENGTH) {
      ToastError(`Inhoud mag niet langer zijn dan ${MAX_CONTENT_LENGTH} tekens`);
      return;
    }

    if (name === 'receiver_email') {
      if (value === 'custom') {
        setIsCustomEmail(true);
        setEmailData(prevState => ({ ...prevState, receiver_email: '' }));
      } else {
        setIsCustomEmail(false);
        setEmailData(prevState => ({ ...prevState, receiver_email: value }));
        setEmailError('');
      }
    } else if (name === 'custom_email') {
      if (value.length > MAX_EMAIL_LENGTH) {
        setEmailError(`E-mailadres mag niet langer zijn dan ${MAX_EMAIL_LENGTH} tekens`);
        return;
      }
      setEmailData(prevState => ({ ...prevState, receiver_email: value }));
      if (value && !validateEmail(value)) {
        setEmailError('Voer een geldig e-mailadres in');
      } else {
        setEmailError('');
      }
    } else {
      setEmailData(prevState => ({ ...prevState, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setTouched({
      receiver_email: true,
      subject: true,
      content: true
    });

    if (!emailData.receiver_email) {
      ToastError('vul de ontvanger in');
      return;
    }

    if (!emailData.subject || emailData.subject.trim() === '') {
      ToastError('Vul het onderwerp in');
      return;
    }

    if (emailError) {
      return;
    }

    if (!emailData.content || emailData.content.trim() === '') {
      ToastError('Vul het bericht in');
      return;
    }

    if (emailData.subject.length > MAX_SUBJECT_LENGTH) {
      ToastError(`Onderwerp mag niet langer zijn dan ${MAX_SUBJECT_LENGTH} tekens`);
      return;
    }
    if (emailData.content.length > MAX_CONTENT_LENGTH) {
      ToastError(`Inhoud mag niet langer zijn dan ${MAX_CONTENT_LENGTH} tekens`);
      return;
    }

    if (!emailData.order_id) {
      ToastError('Geen geldig order ID gevonden');
      return;
    }

    setIsEmailSending(true);

    try {
      const response = await middleware.post('/orders/emails', {
        ...emailData,
        sender_email: senderEmail || DEFAULT_SENDER_EMAIL
      });

      if (response.status !== 200) {
        throw new Error('Failed to send email');
      }
      ToastSuccess("E-mail is succesvol verzonden.");

      if (handleEmailSent) {
        handleEmailSent();
      }

      closeModal();
    } 
    catch (err) {
      console.error('Email sending error:', err);
      ToastError(`Er is een fout opgetreden bij het verzenden van de e-mail: ${err.message}`);
    } finally {
      setIsEmailSending(false);
    }
  };

  return (
    <>
      <ButtonBlue text={"Nieuwe mail"} onClickFunction={() => handleShow()} />
      <Modal
        id="modalNoteCreation"
        show={show}
        onHide={handleClose}
        className="colormodals"
        size="xl"
        centered
      >
        <form>
          <Modal.Header className="modalTop justify-content-between px-5" >
            <h2 className='BlockTitle'>Nieuwe e-mail</h2>
            <ButtonBlue onClickFunction={handleClose} icon={close} />
          </Modal.Header>
          <Modal.Body className="modalCenter">
            {showPreview ? (
              <div className="email-preview">
                <p style={wrapStyle}><strong>Van:</strong> {senderEmail || DEFAULT_SENDER_EMAIL}</p>
                <p style={wrapStyle}><strong>Aan:</strong> {emailData.receiver_email}</p>
                <p style={wrapStyle}><strong>Onderwerp:</strong> {emailData.subject}</p>
                <hr />
                <p style={contentStyle}>{emailData.content}</p>
              </div>
            ) : (
              <>
                <BorderedTextInput
                  label='Verzender'
                  inputValue={senderEmail || DEFAULT_SENDER_EMAIL}
                  setInputValue={() => {}}
                  disabled={true}
                />
                <BorderedDropDown
                  label='Ontvanger'
                  placeholder='Selecteer een ontvanger'
                  options={[
                    ...emails.map(email => ({ title: email.email, value: email.email })),
                    { title: 'Ander e-mailadres', value: 'custom' }
                  ]}
                  selectedValue={isCustomEmail ? 'custom' : emailData.receiver_email}
                  setSelectedValue={val => {
                    if (val === 'custom') {
                      setIsCustomEmail(true);
                      setEmailData(prev => ({ ...prev, receiver_email: '' }));
                    } else {
                      setIsCustomEmail(false);
                      setEmailData(prev => ({ ...prev, receiver_email: val }));
                    }
                  }}
                />
                {isCustomEmail && (
                  <BorderedTextInput
                    label='Custom e-mail'
                    inputValue={emailData.receiver_email}
                    setInputValue={val => setEmailData(prev => ({ ...prev, receiver_email: val }))}
                  />
                )}
                <BorderedTextInput
                  label='Onderwerp'
                  inputValue={emailData.subject}
                  setInputValue={val => setEmailData(prev => ({ ...prev, subject: val }))}
                />
                <label className="mb-1">Bericht</label>
                <textarea
                  className="standardInput"
                  style={{ minHeight: "150px" }}
                  name="content"
                  value={emailData.content}
                  onChange={e => setEmailData(prev => ({ ...prev, content: e.target.value }))}
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="modalBottom">
            <div className="d-flex justify-content-between w-100">
              <ButtonBlue
                text={showPreview ? "Terug naar bewerken" : "Voorbeeld van e-mail"}
                onClickFunction={() => setShowPreview(!showPreview)}
              />
              <div className="d-flex gap-2">
                <ButtonLightGrey text={"Annuleren"} onClickFunction={handleClose} />
                {!showPreview && (
                  <ButtonBlue text={"Verzenden"} onClickFunction={handleSubmit} disabled={isEmailSending} />
                )}
              </div>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}

export default SendEmailModal;